import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MoneyValue } from './MoneyValue';
import { PrintProgress } from './PrintProgress';
import { ReceiptPreview } from './ReceiptPreview';
import { usePrintBatch, useReprintSingle } from '@/hooks/mutations';
import { getSaleDetail } from '@/api/sale.api';
import { useDeviceStore } from '@/stores/device.store';
import { useCartStore } from '@/stores/cart.store';
import { useHotkeys } from '@/hooks/useHotkeys';
import { printReceipt } from '@/lib/printReceipt';
import type { PrintTicketStatus } from '@/api/qrCode.api';
import type { CreateSaleResult } from '@/types/sale';

interface PaidScreenProps {
  result: CreateSaleResult;
}

/** Success + print flow. The sale already succeeded — never block on print. */
export function PaidScreen({ result }: PaidScreenProps) {
  const navigate = useNavigate();
  const printerBound = useDeviceStore((s) => Boolean(s.ticketPrinterId));
  const clearCart = useCartStore((s) => s.clearCart);

  const { mutate: printBatch, isPending: printing } = usePrintBatch();
  const { mutate: reprintOne } = useReprintSingle();

  const [tickets, setTickets] = useState<PrintTicketStatus[]>([]);
  // A REJECTED batch produced nothing at all: onSuccess never fired, the
  // ticket list stayed empty and the panel showed a bare heading. The cashier
  // was left with no tickets, no error and no way to retry.
  const [batchError, setBatchError] = useState<string | null>(null);
  const started = useRef(false);
  const receiptPrinted = useRef(false);

  // Sale detail for the receipt — a QUERY, not a one-shot mutation. Right after
  // a sale the detail can briefly race (SaleItems/QRs still committing), and a
  // mutation fired once in an effect (StrictMode double-invoke + the started
  // guard) could drop its result on the live instance. The query is
  // StrictMode-safe and retries the fresh-sale race until the receipt is ready.
  const { data: detail = null } = useQuery({
    queryKey: ['saleDetail', result.invoiceId],
    queryFn: () => getSaleDetail(result.invoiceId),
    retry: 4,
    retryDelay: 600,
    staleTime: 60_000,
  });

  const doPrint = () => {
    if (!printerBound) return;
    setBatchError(null);
    printBatch(result.invoiceId, {
      onSuccess: (res) => setTickets(res.tickets ?? []),
      onError: (err) => setBatchError(err instanceof Error ? err.message : String(err)),
    });
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    doPrint(); // tickets → the bound gate/ticket printer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-print the thermal receipt once the sale detail is loaded — fires once.
  // Silent when Chrome runs with --kiosk-printing (see lib/printReceipt). The
  // short delay lets the hidden #receipt-print clone paint before print().
  useEffect(() => {
    if (!detail || receiptPrinted.current) return;
    receiptPrinted.current = true;
    const t = setTimeout(() => printReceipt(), 350);
    return () => clearTimeout(t);
  }, [detail]);

  const newSale = () => {
    clearCart();
    navigate('/', { replace: true });
  };

  useHotkeys({ f9: newSale });

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto px-6 py-8 pos-scroll">
      <div className="mb-6 flex animate-gold-pulse flex-col items-center gap-2 rounded-2xl p-4 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <h1 className="font-display text-3xl font-bold text-ink-900">Pembayaran Berhasil</h1>
        <p className="tnum text-sm text-ink-400">Invoice {result.invoiceId}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3 pt-5">
              {result.cash && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-secondary px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Tunai</p>
                    <MoneyValue value={result.cash.amountTendered} className="text-xl" />
                  </div>
                  <div className="rounded-lg bg-gold-200 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Kembalian</p>
                    <MoneyValue value={result.cash.changeDue} emphasis className="text-xl" />
                  </div>
                </div>
              )}
              {typeof detail?.totalAmount === 'number' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase text-ink-600">Total dibayar</span>
                  <MoneyValue value={detail.totalAmount} emphasis className="text-2xl" />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5">
              {printerBound ? (
                <PrintProgress
                  tickets={tickets}
                  isPrinting={printing}
                  batchError={batchError}
                  onRetry={doPrint}
                  onReprintOne={(id) =>
                    reprintOne(id, {
                      onSuccess: (s) =>
                        setTickets((prev) => prev.map((t) => (t.id === id ? s : t))),
                    })
                  }
                />
              ) : (
                <p className="rounded-md border-2 border-warning/30 bg-warning/5 px-4 py-3 text-sm font-medium text-warning">
                  Printer belum diatur — tiket dapat dicetak ulang nanti dari menu Pesanan.
                </p>
              )}
            </CardContent>
          </Card>

          <Button size="lg" variant="gold" className="h-16 w-full text-xl" onClick={newSale}>
            <Plus className="h-6 w-6" /> Transaksi Baru
            <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F9</kbd>
          </Button>
        </div>

        <div>{detail && <ReceiptPreview detail={detail} />}</div>
      </div>

      {/* Print-only clone — the browser prints just this subtree (see the
          .printing-receipt thermal rules in index.css). Fed by the auto-print
          effect above and the reprint path. */}
      {detail && (
        <div id="receipt-print" aria-hidden="true" className="hidden">
          <ReceiptPreview detail={detail} />
        </div>
      )}
    </div>
  );
}
